import { isMobile } from 'react-device-detect';
import { toast } from 'react-toastify';

import logger from 'logger';
import { translate } from 'services/i18nService';
import MessagingService from 'services/MessagingService/MessagingService';
import {
  GENERIC_ERROR,
  GENERIC_SUCCESS,
} from 'services/MessagingService/EventNames';
import {
  CONSOLE_ERRORS,
  POSITION,
  TYPE,
  SUBTYPE,
} from 'enum/notificationsSettings';

/**
 * @class NotificationService used for dispatching and displaying notifications
 */
class NotificationService {
  /**
   * Constructor of @NotificationService class.
   * Initializes properties
   */
  constructor() {
    /**
     * @property {object} events - stores arrays of handler functions for each event e.g.:
     * @property {array} events[eventName]
     */
    this.events = {};
    this.messagingService = MessagingService();
    this.messagingService.on(GENERIC_ERROR, this._handleGenericError);
    this.messagingService.on(GENERIC_SUCCESS, this._handleGenericSuccess);

    /**
     * @property {object} type - stores type constants
     */
    this.type = TYPE;

    /**
     * @property {object} subtype - stores subtype constants
     */
    this.subtype = SUBTYPE;

    /**
     * @property {object} position - stores position constants for toasts
     */
    this.position = POSITION;

    /**
     * @property {object} config - contains default settings for each notification type e.g.:
     * @property {object} config[type]
     */
    this.config = {
      toast: {
        /**
         * @todo Add more defaults from documentation
         */
        subtype: SUBTYPE.SUCCESS,
        autoClose: 4000,
        draggable: false,
        position: POSITION.TOP_RIGHT,
        closeButton: true,
        onClose: 'defaultHandler',
        toastId: isMobile ? 1 : undefined,
        theme: 'colored',
        isDefault: false,
        className: null,
        bodyClassName: null,
      },
      snackbar: {
        // pushNotification
        snackbarType: 'feedbackSnippet',
        subtype: 'default',
        open: true,
        autoHideDuration: 6000,
        contentProps: null,
        transition: 'grow',
        action: null,
        actionClick: null,
        anchorOrigin: {
          vertical: POSITION.BOTTOM,
          horizontal: POSITION.CENTER,
        },
        className: null,
        message: null,
        messageContentId: '',
        onClose: 'defaultHandler',
        messageClass: null,
        actionClass: null,
        closeOnClickAway: true,
        theme: 'colored',
      },
      html5: {},
    };

    /**
     * @property {object} notificationMap - stores queued notifications
     */
    this.notificationMap = { snackbar: [] };

    /**
     * @property {object} blocked - stores flags for the blocked notification types
     */
    this.blocked = { snackbar: false };
  }

  destructor() {
    this.messagingService.off(GENERIC_ERROR, this._handleGenericError);
    this.messagingService.off(GENERIC_SUCCESS, this._handleGenericSuccess);
  }

  /**
   * @callback onEvent - Callback function to execute on specified event
   */
  /**
   * @method
   * Handles subscribing to events, e.g. 'showNotification' which is dispatched when notification is ready to be shown
   * @param {string} event
   * @param {onEvent} callback
   * @returns {function} - for unsubscribing to event
   */
  subscribe(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);

    return {
      unsubscribe: () => {
        this.events[event] = this.events[event].filter((eventCb) => callback !== eventCb);
        if (!this.events[event].length) {
          delete this.events[event];
        }
      },
    };
  }

  /**
   * @method
   * Used to create a new notification by passing the neccessary settings to the service
   * Dispatches event with updated notification settings for the NotificationRenderer
   * @param {object} settings - settings.type & settings.text are mandatory
   */
  create(settings) {
    if (!settings || typeof settings !== 'object') {
      logger.error(CONSOLE_ERRORS.SETTINGS_TYPE);

      return;
    }

    const { type, text, message } = settings;

    if (!type || !(text || message)) {
      logger.error(CONSOLE_ERRORS.MISSING_PROPERTY);

      return;
    }

    if (!this.config[type]) {
      logger.error(CONSOLE_ERRORS.UNKNOWN_TYPE);

      return;
    }

    if (this.blocked[type]) {
      this.notificationMap[type].push(this.prepareNotification(settings));
    } else {
      this.pushForward(this.prepareNotification(settings));
    }
  }

  /**
   * Closes the notification identified by toastId or all the notifications
   * @param {string} toastId , if toastId is undefined, it will close all the notifications
   */
  closeToast = (toastId) => {
    toast.dismiss(toastId);
  };

  /**
   * @method
   * Prepares settings (default config settings get overriden by the custom ones where present)
   * @param {object} settings - custom notification settings
   * @returns {object} - combined settings
   */
  prepareNotification(settings) {
    const { type, text, ...customConfig } = settings;

    // deletes keys which are not expected (dummy-proofing)
    Object.keys(customConfig).forEach((key) => {
      if (typeof this.config[type][key] === 'undefined') {
        delete customConfig[key];
      }
    });

    const notificationSetup = {
      type,
      config: {
        ...this.config[type],
        ...customConfig,
      },
    };

    if (type === TYPE.TOAST) {
      notificationSetup.text = text;
    }

    return notificationSetup;
  }

  /**
   * @method
   * Depending on notification type, sets flag within 'blocked' object to indicate that the current type of
   * notificaation is now blocked, then passes the settings onward to dispatch()
   * @param {object} settings - custom notification settings
   */
  pushForward(settings) {
    if (settings.type === TYPE.SNACKBAR) {
      this.blocked[settings.type] = true;
    }
    this.dispatch('showNotification', settings);
  }

  /**
   * @method
   * Passes data to each handler function of the dispatched event
   * @param {string} event
   * @param {object} data
   */
  dispatch(event, data) {
    if (!this.events[event]) {
      return;
    }
    this.events[event].forEach((callback) => callback(data));
  }

  /**
   * @method
   * Sets the blocked tracker for the type to false.
   * If there are still notifications of that type waiting to be displayed, calls pushForward()
   * @param {string} type
   */
  unblock(type) {
    this.blocked[type] = false;
    if (this.notificationMap[type] && this.notificationMap[type].length) {
      const settings = this.notificationMap[type].shift();
      this.pushForward(settings);
    }
  }

  _handleGenericError = () => {
    this.create({
      type: this.type.TOAST,
      text: translate('homepage.v2.errors.generic'),
      subtype: this.subtype.ERROR,
    });
  };

  _handleGenericSuccess = (translationKey) => {
    if (!translationKey) {
      logger.error('translationKey not provided');

      return;
    }
    this.create({
      type: this.type.TOAST,
      text: translate(translationKey),
      subtype: this.subtype.SUCCESS,
    });
  };

  closeSnackbar = () => {
    this.dispatch('closeSnackbar');
  };
}

export default new NotificationService();
